import {
  faBars,
  faChevronDown,
  faRightFromBracket,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useRef, useState } from "react";
import {
  Link,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";

import { useAuth } from "../auth";
import { titleForPath, useDocumentTitle } from "../document-title";
import { privacyPolicyUrl, termsOfServiceUrl } from "../legal";
import { profileImageChangedEvent } from "../profile-image";
import { Brand } from "./Brand";
import { LegalPopupLink } from "./LegalPopupLink";

export function Layout() {
  const { user, loading, error: sessionError, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const header = useRef<HTMLElement>(null);
  const mobileMenuButton = useRef<HTMLButtonElement>(null);
  const profileMenu = useRef<HTMLDetailsElement>(null);
  const [open, setOpen] = useState(false);
  const [logoutBusy, setLogoutBusy] = useState(false);
  const [logoutError, setLogoutError] = useState("");
  const [avatarVersion, setAvatarVersion] = useState(0);
  const [failedAvatarUserId, setFailedAvatarUserId] = useState<string | null>(
    null,
  );
  useDocumentTitle(titleForPath(location.pathname));
  useEffect(() => {
    const refreshAvatar = () => {
      setFailedAvatarUserId(null);
      setAvatarVersion((value) => value + 1);
    };
    window.addEventListener(profileImageChangedEvent, refreshAvatar);
    return () =>
      window.removeEventListener(profileImageChangedEvent, refreshAvatar);
  }, []);
  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      mobileMenuButton.current?.focus();
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [open]);
  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);
  useEffect(() => {
    const closeMenusOutsideHeader = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Node) || header.current?.contains(target)) return;
      setOpen(false);
      if (profileMenu.current) profileMenu.current.open = false;
    };
    document.addEventListener("pointerdown", closeMenusOutsideHeader);
    return () =>
      document.removeEventListener("pointerdown", closeMenusOutsideHeader);
  }, []);
  const close = () => {
    setOpen(false);
    if (profileMenu.current) profileMenu.current.open = false;
  };
  const manageTarget = user ? "/library?scope=mine" : "/sign-in";
  const displayName = user
    ? `${user.firstName} ${user.lastName}`.trim() || user.email.split("@")[0]
    : "";
  const displayOrganization = user
    ? user.account.organization || user.account.name
    : "";
  const initials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase() ||
      user.email.charAt(0).toUpperCase()
    : "";
  const handleLogout = async () => {
    if (logoutBusy) return;
    setLogoutBusy(true);
    setLogoutError("");
    try {
      await logout();
      close();
      navigate("/");
    } catch {
      setLogoutError("Unable to sign out. Try again.");
    } finally {
      setLogoutBusy(false);
    }
  };

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <header ref={header} className="site-header">
        <div className="container header-inner">
          <Brand />
          <button
            ref={mobileMenuButton}
            className="icon-button mobile-menu"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            aria-controls="primary-navigation"
            aria-label="Toggle navigation"
          >
            <FontAwesomeIcon icon={faBars} />
          </button>
          <nav
            id="primary-navigation"
            className={open ? "main-nav is-open" : "main-nav"}
            aria-label="Main navigation"
          >
            <NavLink to="/features" onClick={close}>
              Features
            </NavLink>
            <NavLink to="/pricing" onClick={close}>
              Pricing
            </NavLink>
            <NavLink to="/library" onClick={close}>
              Published Agenda
            </NavLink>
            <Link
              className="button button-small"
              to={manageTarget}
              aria-disabled={loading || undefined}
              state={
                user
                  ? undefined
                  : {
                      backgroundLocation: location,
                      from: "/library?scope=mine",
                    }
              }
              onClick={(event) => {
                if (loading) {
                  event.preventDefault();
                  return;
                }
                close();
              }}
            >
              Manage
            </Link>
            {user ? (
              <details
                ref={profileMenu}
                className="profile-menu"
                onKeyDown={(event) => {
                  if (event.key !== "Escape") return;
                  event.preventDefault();
                  close();
                  profileMenu.current?.querySelector("summary")?.focus();
                }}
              >
                <summary>
                  <span className="header-profile-avatar" aria-hidden="true">
                    <span>{initials}</span>
                    {failedAvatarUserId !== user.id ? (
                      <img
                        key={user.id}
                        src={
                          avatarVersion
                            ? `/api/account/profile-image?v=${String(avatarVersion)}`
                            : "/api/account/profile-image"
                        }
                        alt=""
                        onError={() => setFailedAvatarUserId(user.id)}
                      />
                    ) : null}
                  </span>
                  <span className="header-profile-copy">
                    <strong>{displayName}</strong>
                    <small>{displayOrganization}</small>
                  </span>
                  <FontAwesomeIcon icon={faChevronDown} />
                </summary>
                <div>
                  <Link to="/account" onClick={close}>
                    Account Settings
                  </Link>
                  <button
                    disabled={logoutBusy}
                    onClick={() => void handleLogout()}
                  >
                    <FontAwesomeIcon icon={faRightFromBracket} />{" "}
                    {logoutBusy ? "Signing out…" : "Logout"}
                  </button>
                  {logoutError ? (
                    <p className="profile-menu-error" role="alert">
                      {logoutError}
                    </p>
                  ) : null}
                </div>
              </details>
            ) : loading ? (
              <span className="profile-link session-checking" role="status">
                <span className="avatar">
                  <FontAwesomeIcon icon={faUser} />
                </span>
                <span>Checking access…</span>
              </span>
            ) : (
              <Link
                className="profile-link"
                to="/sign-in"
                state={{ backgroundLocation: location }}
                onClick={close}
              >
                <span className="avatar">
                  <FontAwesomeIcon icon={faUser} />
                </span>
                <span>Sign in</span>
              </Link>
            )}
          </nav>
        </div>
      </header>
      {sessionError ? (
        <div className="session-status" role="alert">
          <div className="container">
            <span>{sessionError}</span>
            <button
              className="text-button"
              disabled={loading}
              onClick={() => void refresh().catch(() => undefined)}
            >
              {loading ? "Retrying…" : "Retry"}
            </button>
          </div>
        </div>
      ) : null}
      <main id="main-content">
        <Outlet />
      </main>
      <footer className="site-footer">
        <div className="container footer-inner">
          <p className="copyright">
            Copyright {new Date().getFullYear()} StanceKit
            <span aria-hidden="true">·</span>
            <span>
              Independent position development and voter-guide publishing.
            </span>
          </p>
          <nav aria-label="Legal navigation">
            <Link to="/about">About</Link>
            <LegalPopupLink href={termsOfServiceUrl}>
              Terms of service
            </LegalPopupLink>
            <LegalPopupLink href={privacyPolicyUrl}>
              Privacy policy
            </LegalPopupLink>
            <Link to="/cookies">Cookies</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
